// One busy state for every Turbo form submit (DESIGN.md BUTTON-8). Turbo
// disables the submitter while the request is in flight, which alone painted it
// as the grey disabled button with no spinner and no aria-busy. This hook marks
// it busy instead — spinner in the icon's place, label unchanged,
// aria-busy="true" — and `_buttons.scss` keeps a busy button's rest paint.
//
// Rules:
//   * the submitter is `event.detail.formSubmission.submitter`; a
//     `form.requestSubmit()` with no submitter falls back to the form's own
//     submit button (Turbo disables nothing then, so aria-busy's
//     `pointer-events: none` is what blocks a second click);
//   * skipped: a form or submitter under `data-turbo="false"` (Stripe checkout
//     and the like redirect away natively), a button that manages its own busy
//     state (`data-busy-managed`, on the button or the form), a button already
//     busy, and a `data-turbo-submits-with` button (Turbo swaps and restores
//     that one's label itself);
//   * restored on `turbo:submit-end` in the CAPTURE phase, before any form-level
//     listener: card-dirty's re-baseline then sees the settled button and
//     decides its disabled state last. While busy, card-dirty never re-opens
//     the Save (the lib/gate_reason rule, #18139).

import { clearButtonBusy, isButtonBusy, markButtonBusy } from "./button_loading"

export const BUSY_MANAGED_SELECTOR = "[data-busy-managed]"
const TURBO_OFF_SELECTOR = '[data-turbo="false"]'
const SUBMIT_BUTTON_SELECTOR = 'button[type="submit"], button:not([type]), input[type="submit"]'

// The buttons this hook marked, so it never clears a busy state it did not set.
const marked = new WeakSet()

function submitterFor(formSubmission) {
  if (!formSubmission) return null
  if (formSubmission.submitter) return formSubmission.submitter
  const form = formSubmission.formElement
  if (!form) return null
  return form.querySelector(SUBMIT_BUTTON_SELECTOR) ||
    (form.id ? document.querySelector(`[form="${CSS.escape(form.id)}"]:is(${SUBMIT_BUTTON_SELECTOR})`) : null)
}

function skip(button, form) {
  if (!button) return true
  if (isButtonBusy(button)) return true
  if (button.hasAttribute("data-turbo-submits-with")) return true
  if (button.closest(BUSY_MANAGED_SELECTOR) || form?.closest(BUSY_MANAGED_SELECTOR)) return true
  if (button.closest(TURBO_OFF_SELECTOR) || form?.closest(TURBO_OFF_SELECTOR)) return true
  return false
}

export function onTurboSubmitStart(event) {
  const formSubmission = event.detail?.formSubmission
  const button = submitterFor(formSubmission)
  if (skip(button, formSubmission?.formElement)) return
  markButtonBusy(button)
  marked.add(button)
}

export function onTurboSubmitEnd(event) {
  const button = submitterFor(event.detail?.formSubmission)
  if (!button || !marked.has(button)) return
  marked.delete(button)
  clearButtonBusy(button)
}

let installed = false

export function initTurboSubmitBusy() {
  if (installed) return
  installed = true
  document.addEventListener("turbo:submit-start", onTurboSubmitStart, true)
  document.addEventListener("turbo:submit-end", onTurboSubmitEnd, true)
}
